<template>
  <oct-dialog
    full
    :title="title"
    align="left"
    icon="arrow-left"
    @dismiss="$_octPrototypeView_goBack"
    class="ss-user-list"
  >
    <!-- Sorter -->
    <div class="ss-user-list__actions">
      <oct-select
        block
        dense
        v-model="order"
        :items="[
          {
            value: 'desc',
            text: '新しい順'
          },
          {
            value: 'asc',
            text: '古い順'
          }
        ]"
      />
      
      <oct-select
        block
        dense
        v-model="role"
        :items="[
          {
            value: '',
            text: '全てのスタッフ'
          },
          {
            value: '1',
            text: 'オーナー'
          },
          {
            value: '2',
            text: 'スタイリスト'
          },
          {
            value: '3',
            text: 'アシスタント'
          },
          {
            value: '4',
            text: 'スタッフ'
          }
        ]"
      />
    </div>

    <!-- List -->
    <oct-list>
      <oct-list-item
        v-for="item in items"
        :key="item.id"
        :media="{
          image: $_octPrototypeView_getProfileIconSafely(item.profileIcon)
        }"
        :title="`${item.lastName} ${item.firstName}`"
        :text="$_octPrototypeView_getRoleNameById(item.role)"
        :value="item.id"
        selectable
        :disabled="item.status === 2"
        v-model="$data.$_ssULV_selectedItems"
        @click.native="$router.push({
          path: `${$route.path}/${item.id}`
        }).catch(() => {})"
      >
        <!-- Meta -->
        <template #meta>
          <oct-chip
            selected
            v-if="item.status === 2"
          >
            未登録
          </oct-chip>

          <span
            class="ss-user-list-stutus-indicator ss-user-list-stutus-indicator--danger"
            v-else-if="item.status === 1"
          >
            停止中
          </span>

          <oct-chip
            outline
            selected
            v-else-if="item.data.verified === false"
          >
            未承認
          </oct-chip>

          <span
            class="ss-user-list-stutus-indicator ss-user-list-stutus-indicator--success"
            v-else
          >
            稼働中
          </span>
        </template>

        <!-- Trailing icon -->
        <template #trailing-icon>
          <oct-icon icon="chevron-right"/>
        </template>
      </oct-list-item>
    </oct-list>

    <!-- Child routes -->
    <transition :name="transitionName">
      <router-view class="ss-dialog__content" :key="$route.name"/>
    </transition>

    <!-- Actions -->
    <template
      #actions
    >
      <!-- Actions (Default) -->
      <div
        class="ss-user-list-footer__actions"
        v-if="!$data.$_ssULV_selectedItems.length"
      >
        <!-- Sign Up URL -->
        <div class="ss-user-list-footer__section">
          <!-- URL -->
          <oct-text-field
            :value="$data.$_ssULV_signUpURL"
            block
            class="ss-user-list-footer__text-field"
          />

          <!-- Refresh -->
          <oct-button
            class="ss-user-list-footer__icon-button"
            icon="refresh"
            outline
            @click.native="$_ssUSV_setSignUpURL(true)"
          />

          <!-- Copy -->
          <oct-button
            class="ss-user-list-footer__button"
            outline
            @click.native="$_ssUSV_copyToClipboard($data.$_ssULV_signUpURL)"
          >
            URLコピー
          </oct-button>
        </div>

        <!-- Primary Action -->
        <div>
          <oct-button
            block
            size="large"
            @click.native="$router.push({
              path: `${$route.path}/new`
            }).catch(() => {})"
          >
            新規スタッフ追加
          </oct-button>
        </div>
      </div>

      <!-- Actions (Status Modification) -->
      <div
        class="ss-user-list-footer__actions"
        v-else
      >
        <div class="ss-user-list-footer__section">
          <!-- Text -->
          <div class="ss-user-list-footer__text">
            <oct-checkbox
              checked
              class="ss-user-list-footer__checkbox"
            />

            <p class="ss-user-list-footer__secondary-text">
              {{ $data.$_ssULV_selectedItems.length }} 個の項目を選択中
            </p>
          </div>

          <!-- Select -->
          <oct-select
            block
            dense
            :items="[
              {
                value: 0,
                text: '-- 選択してください --'
              },
              {
                value: 1,
                text: '停止中'
              },
              {
                value: 4,
                text: '稼働中'
              }
            ]"
            v-model="$data.$_ssULV_selectedStatus"
          />
        </div>

        <!-- Primary Action -->
        <div>
          <oct-button
            block
            size="large"
            @click.native="$_ssUSV_onClickChangeMultipleStatus"
          >
            保存
          </oct-button>
        </div>
      </div>
    </template>
  </oct-dialog>
</template>

<script>
import _cloneDeep from 'lodash/cloneDeep'
import API from '@/api/index.js'
import OctIcon from '@/components/icon/OctIcon.vue'
import OctButton from '@/components/button/OctButton.vue'
import OctChip from '@/components/chips/OctChip.vue'
import OctList from '@/components/list/OctList.vue'
import OctTextField from '@/components/text-field/OctTextField.vue'
import OctSelect from '@/components/select/OctSelect.vue'
import OctCheckbox from '@/components/checkbox/OctCheckbox.vue'
import OctListItem from '@/components/list/OctListItem.vue'
import OctDialog from '@/components/dialog/OctDialog.vue'
import OctPrototypeView from '@/mixins/OctPrototypeView.js'

export default {
  name: 'SSUserListView',
  mixins: [
    OctPrototypeView
  ],
  components: {
    OctIcon,
    OctButton,
    OctChip,
    OctTextField,
    OctSelect,
    OctCheckbox,
    OctList,
    OctListItem,
    OctDialog
  },
  props: {
    /** Page title */
    title: {
      type: String,
      default: ''
    }
  },
  data () {
    return {
      // Sign up URL
      $_ssULV_signUpURL: '',
      // Sort order
      $order: this.$route.meta.order,
      // Indicate the role of users to show
      $role: this.$route.meta.role,
      // Selected items
      $_ssULV_selectedItems: [],
      // Selected status
      $_ssULV_selectedStatus: 0,
    }
  },
  computed: {
    order: {
      get () {
        return this.$data.$order ||  'desc'
      },
      set (val) {
        this.$data.$order = this.$route.meta.order = val
      }
    },
    role: {
      get () {
        return this.$data.$role ||  ''
      },
      set (val) {
        this.$data.$role = this.$route.meta.role = val
      }
    },
    // Array of users filtered by *order and *role
    items: vm => vm.$store.state.staffs.all.concat()
                  // Filter by role
                  .filter(item => !vm.role || item.role == vm.role)[vm.order === 'asc' ? 'reverse' : 'concat']()
  },
  methods: {
    /** Set sign up URL */
    async $_ssUSV_setSignUpURL (refresh=false) {
      try {
        const token = await this.$_ssUSV_getSalonToken(refresh)
        this.$data.$_ssULV_signUpURL = `${location.origin}/#signup/${this.$store.state.id}/${token}?salon=${this.$store.state.name}`
      } catch {
        this.$store.dispatch('alert', '新規スタッフ追加URLを設定できませんでした。しばらく経ってからもう一度やり直してください。')
      }
    },
    /** Get salon token */
    async $_ssUSV_getSalonToken (refresh=false) {
      return (await API.getSalonToken({id: this.$store.state.id, refresh})).token
    },
    /** Click handler of the multiple state change button */
    async $_ssUSV_onClickChangeMultipleStatus () {
      // Ignore invalid status
      if (!this.$data.$_ssULV_selectedStatus) return

      try {
        // Confirmation
        await this.$store.dispatch('confirm', '本当にステータスを変更しますか？')

        try {
          // Update status
          await Promise.all(
            this.$data.$_ssULV_selectedItems.map(item => {
              let target = this.$store.getters['staffs/byId'](item)

              // Invalid user
              if (!target) return Promise.reject()

              // Create data to update
              target = _cloneDeep(target)
              target.status = this.$data.$_ssULV_selectedStatus
              target.data.verified = true

              return this.$store.dispatch('staffs/save', {item: target})
            })
          )

          // Notify
          this.$store.dispatch('notify', {text: 'ステータスを変更しました'})
        } catch (thrown) {
          console.error(thrown)
          // Alert
          this.$store.dispatch('alert', 'ステータスを変更できませんでした。しばらく経ってからもう一度やり直してください。')
        } finally {
          // Restore variables
          this.$data.$_ssULV_selectedStatus = 0
          this.$data.$_ssULV_selectedItems = []
        }
      } catch {/* Do nothing */}
    },
    /** Copy text to clipboard */
    $_ssUSV_copyToClipboard (text) {
      navigator.clipboard && navigator.clipboard.writeText(text)
      // Notify
      this.$store.dispatch('notify', {text: 'コピーしました'})
    }
  },
  async mounted () {
    this.$_ssUSV_setSignUpURL()

    /** Remove test staffs
    for (const item of this.$store.state.staffs.all) {
      item.lastName === '巡回' && await this.$store.dispatch('staffs/remove', {item})
    }
    */
  }
}
</script>

<style scoped lang="scss">
@import "../components/theme/variables";
@import "../components/grid/variables";
@import "../components/typography/variables";
@import "../components/typography/mixins";

.ss-user-list {
  &::v-deep .oct-dialog-header {
    margin-bottom: oct-rem(32);
  }

@media screen and (min-width: map-get($grid-breakpoints, 'md')) {
  &::v-deep .oct-dialog-header {
    margin-bottom: oct-rem(16);
  }
}
}

.ss-user-list__actions {
  margin: 0 0 oct-rem(32);
  display: flex;
  column-gap: oct-rem(14);
  padding: 0;

  .oct-select {
    flex: 1 1 auto;
  }
}

/* Containers */
.ss-user-list-footer__section {
  display: flex;
  align-items: center;
  gap: oct-rem(16);
}

/* Elements */
.ss-user-list-stutus-indicator {
  font-size: oct-rem(14);
  line-height: 1;
  display: flex;
  gap: oct-rem(4);
  align-items: center;
  height: oct-rem(38);
  padding: 0 oct-rem(12);

  &:before {
    content: "";
    width: oct-rem(8);
    height: oct-rem(8);
    border-radius: 50%;
  }

  &--success {
    color: $oct-theme--success;
    
    &:before {
      background-color: $oct-theme--success;
    }
  }

  &--danger {
    color: $oct-theme--danger;
    
    &:before {
      background-color: $oct-theme--danger;
    }
  }
}

/* Footer */
.ss-user-list-footer__actions {
  display: flex;
  flex-direction: column;
  gap: oct-rem(24);
  width: 100%;

  ::v-deep .oct-select {
    flex: 1 1 50%;
  }
}

.ss-user-list-footer__icon-button {
  flex: 0 0 auto !important;
  border-color: $oct-theme--neutral-20;
}

.ss-user-list-footer__button {
  flex: 1 1 auto !important;
}

.ss-user-list-footer__text {
  color: $oct-theme--primary;
  flex: 1 1 50%;
  display: flex;
  align-items: center;
  gap: oct-rem(13);
}

.ss-user-list-footer__checkbox {
  pointer-events: none;
}
</style>